import { Component, OnInit, ElementRef, ViewChild, ChangeDetectorRef } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, UntypedFormArray, UntypedFormControl, Validators } from '@angular/forms';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles/enums';
import { Observable, Subscription } from 'rxjs';

import { ButtonType, LoaderTheme, NotificationTheme } from '@fcom/ui-components';
import {
  CheckboxOptionList,
  FormStatus,
  AnalyticsFormStatus,
  TypedFormGroup,
  TypedFormArray,
} from '@fcom/service-forms';
import { GtmService } from '@fcom/common/gtm';
import { trackFormState } from '@fcom/service-forms/services/utils';
import { Profile } from '@fcom/core-api/login';
import { FragmentService, SelectOption } from '@fcom/common';
import { enumValues } from '@fcom/core/utils';
import { CmsArticle } from '@fcom/core-api';
import { finShare } from '@fcom/rx';

import {
  TravelProfileSubmissionData,
  TravelProfileFormField,
  SeatPreferenceOption,
  MealPreferenceOption,
  TopicOfInterest,
} from '../../interfaces/profile.interface';
import { ProfileService } from '../../services/profile.service';

const INITIAL_MAX_CHILDREN = 5;
const HOUSEHOLD_SIZE = 11;
const MAX_CHILD_BIRTH_YEAR = 19;
const ANALYTICS_FORM_NAME = 'update-profile-travel-profile';

@Component({
  selector: 'fin-loyalty-travel-profile-form',
  templateUrl: 'loyalty-travel-profile-form.component.html',
  styleUrls: ['./loyalty-travel-profile-form.component.scss'],
})
export class LoyaltyTravelProfileFormComponent implements OnInit {
  public readonly FormStatus = FormStatus;
  public readonly TravelProfileFormField = TravelProfileFormField;
  public readonly ButtonType = ButtonType;
  public readonly NotificationTheme = NotificationTheme;
  public readonly LoaderTheme = LoaderTheme;
  public readonly SvgLibraryIcon = SvgLibraryIcon;

  public travelProfileDescription$: Observable<CmsArticle>;

  public notification: ElementRef;

  @ViewChild('fcomNotification', { static: false })
  set notificationElem(notificationElem: ElementRef) {
    this.notification = notificationElem;
  }

  reactiveForm: TypedFormGroup<UntypedFormGroup | TypedFormArray<UntypedFormGroup>>;
  seatPreferenceOptions: SelectOption[] = [
    { value: SeatPreferenceOption.NO_PREFERENCE },
    { value: SeatPreferenceOption.AISLE },
    { value: SeatPreferenceOption.WINDOW },
  ];
  mealPreferenceOptions: SelectOption[] = [
    { value: MealPreferenceOption.NO_PREFERENCE },
    { value: MealPreferenceOption.BLML },
    { value: MealPreferenceOption.DBML },
    { value: MealPreferenceOption.GFML },
    { value: MealPreferenceOption.LCML },
    { value: MealPreferenceOption.LFML },
    { value: MealPreferenceOption.LSML },
    { value: MealPreferenceOption.NLML },
    { value: MealPreferenceOption.HNML },
    { value: MealPreferenceOption.AVML },
    { value: MealPreferenceOption.KSML },
    { value: MealPreferenceOption.MOML },
    { value: MealPreferenceOption.CHML },
    { value: MealPreferenceOption.FPML },
    { value: MealPreferenceOption.VGML },
    { value: MealPreferenceOption.VLML },
    { value: MealPreferenceOption.VJML },
    { value: MealPreferenceOption.SPML },
  ];
  householdSizeOptions: SelectOption[];
  birthYears: SelectOption[];
  childrenBirthYearsFormGroupConfig: { [key: string]: any } = { birthYear: ['', [Validators.required]] };
  topicsOfInterestControls: CheckboxOptionList = [
    { id: TopicOfInterest.FOD, label: 'plus.profile.travelProfile.topicsOfInterest.FOD' },
    { id: TopicOfInterest.SPE, label: 'plus.profile.travelProfile.topicsOfInterest.SPE' },
    { id: TopicOfInterest.MUE, label: 'plus.profile.travelProfile.topicsOfInterest.MUE' },
    { id: TopicOfInterest.BEF, label: 'plus.profile.travelProfile.topicsOfInterest.BEF' },
    { id: TopicOfInterest.HOD, label: 'plus.profile.travelProfile.topicsOfInterest.HOD' },
    { id: TopicOfInterest.BUL, label: 'plus.profile.travelProfile.topicsOfInterest.BUL' },
    { id: TopicOfInterest.HEW, label: 'plus.profile.travelProfile.topicsOfInterest.HEW' },
    { id: TopicOfInterest.HOT, label: 'plus.profile.travelProfile.topicsOfInterest.HOT' },
    { id: TopicOfInterest.CAR, label: 'plus.profile.travelProfile.topicsOfInterest.CAR' },
    { id: TopicOfInterest.CIH, label: 'plus.profile.travelProfile.topicsOfInterest.CIH' },
    { id: TopicOfInterest.BEH, label: 'plus.profile.travelProfile.topicsOfInterest.BEH' },
    { id: TopicOfInterest.SPH, label: 'plus.profile.travelProfile.topicsOfInterest.SPH' },
    { id: TopicOfInterest.ACH, label: 'plus.profile.travelProfile.topicsOfInterest.ACH' },
    { id: TopicOfInterest.TOH, label: 'plus.profile.travelProfile.topicsOfInterest.TOH' },
    { id: TopicOfInterest.CRU, label: 'plus.profile.travelProfile.topicsOfInterest.CRU' },
    { id: TopicOfInterest.ENR, label: 'plus.profile.travelProfile.topicsOfInterest.ENR' },
  ];
  formStatus: FormStatus = FormStatus.INITIAL;
  initialMaxChildren: number = INITIAL_MAX_CHILDREN;
  maxChildren = 0;
  tooltipOptions = {
    enableOnClick: true,
    showCloseButton: true,
    tooltipWidth: 342,
    minOffsetLeft: 10,
  };

  private subscriptions = new Subscription();

  constructor(
    private fb: UntypedFormBuilder,
    private profileService: ProfileService,
    private gtmService: GtmService,
    private fragmentService: FragmentService,
    private cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.travelProfileDescription$ = this.fragmentService
      .getFragment<CmsArticle>('LOYALTY.fragments.travelProfileDescription.url')
      .pipe(finShare());

    this.householdSizeOptions = this.generateSelectOptionIntRange(HOUSEHOLD_SIZE); // Household size 0 - 10
    this.birthYears = this.generateSelectOptionIntRange(MAX_CHILD_BIRTH_YEAR, new Date().getFullYear()); // Birth years ranges from the birth year of an 18 year old until the current year
    this.subscriptions.add(
      this.profileService.getProfile().subscribe((profile: Profile) => {
        this.formStatus = FormStatus.INITIAL;
        this.createForm(profile);
        this.onHouseholdSizeChange();
      })
    );
  }

  onSubmit(): void {
    if (this.formStatus === FormStatus.LOADING) {
      return;
    }

    this.formStatus = FormStatus.LOADING;

    const data: TravelProfileSubmissionData = this.parseSubmissionData(this.reactiveForm.value);

    trackFormState(ANALYTICS_FORM_NAME, AnalyticsFormStatus.SEND_ATTEMPT, this.gtmService);

    this.subscriptions.add(
      this.profileService.updateTravelProfile(data).subscribe({
        next: (result) => {
          if (result.status.message === 'OK') {
            this.formStatus = FormStatus.COMPLETE;
            trackFormState(ANALYTICS_FORM_NAME, AnalyticsFormStatus.SUCCESS, this.gtmService);
          } else {
            this.formStatus = FormStatus.ERROR;
            trackFormState(ANALYTICS_FORM_NAME, AnalyticsFormStatus.ERROR, this.gtmService);
          }
        },
        error: () => {
          this.formStatus = FormStatus.ERROR;
          trackFormState(ANALYTICS_FORM_NAME, AnalyticsFormStatus.ERROR, this.gtmService);
        },
        complete: () => {
          this.cdRef.detectChanges();
          this.notification.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        },
      })
    );
  }

  get birthYearsArray(): UntypedFormArray {
    return this.reactiveForm.get(TravelProfileFormField.CHILDREN_BIRTH_YEARS) as UntypedFormArray;
  }

  private createForm(profile: Profile) {
    const controls = this.topicsOfInterestControls.map(
      (control) =>
        new UntypedFormControl(profile.topicsOfInterest ? profile.topicsOfInterest.includes(control.id) : false)
    );

    const mealPreference =
      enumValues(MealPreferenceOption).find((meal) => meal === profile.preferredMealType) ??
      MealPreferenceOption.NO_PREFERENCE;
    this.reactiveForm = this.fb.group({
      [TravelProfileFormField.SEAT_PREF]: [
        profile.preferredSeatType ? profile.preferredSeatType : SeatPreferenceOption.NO_PREFERENCE,
      ],
      [TravelProfileFormField.MEAL_PREF]: [mealPreference],
      [TravelProfileFormField.HOUSEHOLD_SIZE]: [
        profile.householdSize ? this.setHouseHoldSize(+profile.householdSize) : 0,
      ],
      [TravelProfileFormField.CHILDREN_BIRTH_YEARS]: this.fb.array([]),
      [TravelProfileFormField.TOPICS_OF_INTEREST]: new UntypedFormArray(controls),
    }) as TypedFormGroup<UntypedFormGroup | TypedFormArray<UntypedFormGroup>>;

    if (profile.childrenBirthYears && profile.childrenBirthYears.length > 0) {
      this.prefillChildrenBirthYears(profile.childrenBirthYears);
    }
  }

  private onHouseholdSizeChange() {
    this.subscriptions.add(
      this.reactiveForm.get(TravelProfileFormField.HOUSEHOLD_SIZE).valueChanges.subscribe((val) => {
        this.setMaxChildLimit(+val);
      })
    );
  }

  private setHouseHoldSize(householdSize: number) {
    this.setMaxChildLimit(householdSize);
    return householdSize;
  }

  private setMaxChildLimit(householdSize: number) {
    this.maxChildren =
      householdSize <= this.initialMaxChildren ? Math.max(0, householdSize - 1) : this.initialMaxChildren;
  }

  private prefillChildrenBirthYears(years: string[]) {
    years.forEach((year) => {
      this.birthYearsArray.push(this.fb.group({ birthYear: [year, [Validators.required]] }));
    });
  }

  private generateSelectOptionIntRange(amount: number, rangeEnd?: number): SelectOption[] {
    return Array.from(Array(amount).keys()).map((int) => {
      const value = rangeEnd ? rangeEnd - int : int;
      return { name: value.toString(), value: value.toString() };
    });
  }

  private parseSubmissionData(data: { [key: string]: any }): TravelProfileSubmissionData {
    return {
      householdSize: +data[TravelProfileFormField.HOUSEHOLD_SIZE],
      childrenBirthYears: data[TravelProfileFormField.CHILDREN_BIRTH_YEARS].map((prop) => prop.birthYear),
      travelProfile: {
        seatPreference: data[TravelProfileFormField.SEAT_PREF],
        mealPreference: data[TravelProfileFormField.MEAL_PREF],
      },
      topicsOfInterest: this.parseSelectedTopics(data[TravelProfileFormField.TOPICS_OF_INTEREST]),
    };
  }

  private parseSelectedTopics(topics: boolean[]): string[] {
    return this.topicsOfInterestControls
      .filter((_topic, i) => topics.some((checked: boolean, j: number) => checked && i === j))
      .map((topic) => topic.id);
  }
}
